<template>
  <section class="section plant-result">
    <div class="section__heading">
      <h2 class="section__title">
        <a v-if="plantPageUrl" :href="plantPageUrl">{{ displayName }}</a>
        <span v-else>{{ displayName }}</span>
      </h2>
      <div class="link-list__head-right plant-result__actions">
        <span
          v-if="plantVerificationKey"
          class="link-list__badge"
          :class="'link-list__badge--' + verificationBadgeModifier"
        >{{ $t(plantVerificationKey) }}</span>
        <a
          v-if="plant.shopping_link"
          class="chip chip--secondary plant-result__action"
          :href="plant.shopping_link"
          target="_blank"
          rel="nofollow sponsored noopener"
        >{{ $t('shopping') }}</a>
        <button
          type="button"
          class="chip chip--secondary plant-result__action plant-result__action-btn"
          @click="toggleLovedPlant(plant)"
        >
          {{ isLovedPlant(plant) ? '♥' : '♡' }}
        </button>
        <button
          type="button"
          class="chip chip--secondary plant-result__action plant-result__action-btn"
          v-b-modal.plant-modal
          @click="selectPlant(plant)"
        >
          <svg class="chip__icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M2.25 1.25h1.5v13.5h-1.5V1.25zm2.25 1.1 8.4-1.05v6.15l-8.4 1.05V2.35z"/>
          </svg>
          {{ $t('report_short') }}
        </button>
      </div>
    </div>
    <p class="plant-result__latin"><em>{{ plant.scientific_name }}</em></p>

    <PlantImageCarousel
      :images="plant.plantimage_set"
      :alt="displayName"
      :search-query="plant.scientific_name"
    />

    <p v-if="hasAdditionalNames" class="plant-result__names">
      <strong>{{ $t('additional_names') }}:</strong>
      {{ getLangValue(plant, 'additional_names', false) }}
    </p>

    <ul class="link-list">
      <li v-for="animal in visibleAnimals" :key="animal.id">
        <div class="link-list__head">
          <strong>{{ animalName(animal) }}</strong>
          <span
            class="link-list__badge"
            :class="toxicityBadgeClass(animal.is_toxic)"
          >{{ $t(getToxicName(animal.is_toxic)) }}</span>
          <span class="link-list__head-right">
            <span
              class="link-list__badge"
              :class="animal.is_verified ? 'link-list__badge--verified' : 'link-list__badge--unverified'"
            >{{ $t(animal.is_verified ? 'verified' : 'not_verified') }}</span>
          </span>
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
import PlantImageCarousel from './PlantImageCarousel.vue'
import { usePlantStore } from '@/stores/plant-store'
import { mapState } from 'pinia'

export default {
  components: {
    PlantImageCarousel
  },
  props: {
    plant: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapState(usePlantStore, ['selected_animals', 'lang']),
    displayName () {
      return this.getLangValue(this.plant, 'name')
    },
    hasAdditionalNames () {
      const names = this.getLangValue(this.plant, 'additional_names', false)
      return names && names !== '-'
    },
    plantPageUrl () {
      const alias = this.lang === 'en' ? this.plant.alias_en : this.plant.alias
      if (!alias) {
        return null
      }
      return this.lang === 'en' ? '/en/' + alias : '/' + alias
    },
    visibleAnimals () {
      return (this.plant.plantanimal_set || []).filter(
        (item) => this.selected_animals.includes(item.animal.id)
      )
    },
    plantVerificationKey () {
      const animals = this.plant.plantanimal_set || []
      if (!animals.length) {
        return null
      }
      const verifiedCount = animals.filter((item) => item.is_verified).length
      if (verifiedCount === animals.length) {
        return 'verified'
      }
      if (verifiedCount === 0) {
        return 'not_verified'
      }
      return 'partially_verified'
    },
    verificationBadgeModifier () {
      if (this.plantVerificationKey === 'verified') {
        return 'verified'
      }
      return 'unverified'
    }
  },
  methods: {
    selectPlant (plant) {
      usePlantStore().selected_plant = plant
    },
    animalName (plantanimal) {
      const store = usePlantStore()
      const nameKey = 'name_' + store.lang
      if (nameKey in plantanimal.animal && plantanimal.animal[nameKey]) {
        return plantanimal.animal[nameKey]
      }
      return plantanimal.animal.name
    },
    getLangValue (obj, field, useDefault = true) {
      const store = usePlantStore()
      const nameKey = field + '_' + store.lang
      if (nameKey in obj && obj[nameKey]) {
        return obj[nameKey]
      }
      if (useDefault) {
        return obj[field]
      }
      return '-'
    },
    getToxicName (isToxic) {
      if (isToxic == 1) {
        return 'toxic'
      }
      if (isToxic == -1) {
        return 'safe'
      }
      return 'unknown'
    },
    toxicityBadgeClass (isToxic) {
      if (isToxic == 1) {
        return 'link-list__badge--toxic'
      }
      if (isToxic == -1) {
        return 'link-list__badge--safe'
      }
      return 'link-list__badge--suggested'
    },
    isLovedPlant (plant) {
      return usePlantStore().loved_plant_ids.includes(plant.id)
    },
    toggleLovedPlant (plant) {
      if (this.isLovedPlant(plant)) {
        this.removeLovedPlant(plant)
      } else {
        this.addLovedPlant(plant)
      }
    },
    addLovedPlant (plant) {
      const store = usePlantStore()
      store.loved_plant_ids.push(plant.id)
      store.loved_plant_ids = store.loved_plant_ids.filter((value, index, array) => array.indexOf(value) === index)
      localStorage.setItem('loved_plant_ids', JSON.stringify(store.loved_plant_ids))
    },
    removeLovedPlant (plant) {
      const store = usePlantStore()
      store.loved_plant_ids = store.loved_plant_ids.filter(i => i != plant.id)
      localStorage.setItem('loved_plant_ids', JSON.stringify(store.loved_plant_ids))
      if (store.is_loved_plants) {
        store.plants = store.plants.filter(i => i.id != plant.id)
      }
    }
  }
}
</script>
